import { DecimalPipe, NgClass, NgOptimizedImage, NgTemplateOutlet } from '@angular/common';
import {
  Component,
  DestroyRef,
  effect,
  EventEmitter,
  inject,
  Injector,
  Input,
  OnChanges,
  OnInit,
  Output,
  signal,
  SimpleChanges,
  untracked,
  ViewChild,
  WritableSignal
} from '@angular/core';
import {
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import {
  MatExpansionPanel,
  MatExpansionPanelDescription,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle
} from '@angular/material/expansion';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatFooterCell,
  MatFooterCellDef,
  MatFooterRow,
  MatFooterRowDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
  MatTableDataSource
} from '@angular/material/table';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ConfirmBoxComponent } from '../../../components/confirm-box/confirm-box.component';
import { DynamicDialogService } from '../../../components/dynamic-dialog/dynamic-dialog-service';
import { MessagingService } from '../../../components/messaging/messaging.service';
import {
  invoiceCurtainPartColumnHeadersLargeScreen,
  invoiceCurtainPartColumnHeadersSmallScreen
} from '../../../consts/columnHeaders.const';
import { mediumModalConfig, smallModalConfig, wideModalConfig } from '../../../consts/modal-config.const';
import { BreakpointObserverHelper } from '../../../helpers/breakpoint-observer.helper';
import { DateInputHelper } from '../../../helpers/date-input.helper';
import { ModalConfigsHelper } from '../../../helpers/modal-configs.helper';
import { BankingDetailsBanner } from '../../../model/banking-details.model';
import { DocumentInvoiceItem, Invoice, InvoiceLabour, InvoicePart, InvoiceSublet } from '../../../model/document.model';
import {
  DisbursementAccount,
  EstImagePayRequest
} from '../../estimage-pay-page/estimage-pay-invoices/estimage-pay-invoices.model';
import { FactoringCompany } from '../../../model/factoring-company.model';
import { User } from '../../../model/user.model';
import { CurrentUserService } from '../../../services/currentUser.service';
import { SysAdminService } from '../../../services/sys-admin.service';
import { ToolbarService } from '../../../services/toolbar.service';
import { UserService } from '../../../services/user.service';

import { EditBankingDetailsComponent } from '../../location-admin/edit-banking-details/edit-banking-details.component';
import { InvoiceStore } from './invoice.store.component';
import { UseEstimagePayModalComponent } from './use-estimage-pay-modal/use-estimage-pay-modal.component';
import { UseFactoringCompanyModalComponent } from './use-factoring-company-modal/use-factoring-company-modal.component';
import { JobViewerStore } from '../job-viewer.store';
import { HighlightStatusDirective } from '../../../directives/highlight-status.directive';
import { MatTooltip } from '@angular/material/tooltip';
import { PdfButtonComponent } from '../pdf-button/pdf-button.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatMenu, MatMenuContent, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatIcon } from '@angular/material/icon';
import { MatDivider } from '@angular/material/divider';
import { MatError, MatFormField, MatLabel, MatPrefix, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { BrowserAutoFillDisabledDirective } from '../../../directives/browser-auto-fill-disabled.directive';
import { InfosCardComponent } from '../../../components/infos-card/infos-card.component';
import { MatCheckbox } from '@angular/material/checkbox';
import { InfosGuidanceComponent } from '../../../components/infos-guidance/infos-guidance.component';
import { MatTab, MatTabGroup, MatTabLabel } from '@angular/material/tabs';
import { MatCard } from '@angular/material/card';
import { EstimagePayGuidanceComponent } from './estimage-pay-guidance/estimage-pay-guidance.component';
import { FactoringCompanyGuidanceComponent } from './factoring-company-guidance/factoring-company-guidance.component';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { InfosContentComponent } from '../../../components/infos-content/infos-content.component';
import { DatePipe } from '../../../pipes/date.pipe';
import { TimePipe } from '../../../pipes/time.pipe';
import { GetControlPipe } from '../../../pipes/get-control.pipe';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Job } from '../../../model/job.model';

@Component({
  selector: 'app-invoice-curtain',
  templateUrl: './invoice-curtain.component.html',
  styleUrls: ['./invoice-curtain.component.scss'],
  providers: [DecimalPipe],
  imports: [
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    HighlightStatusDirective,
    MatTooltip,
    MatExpansionPanelDescription,
    PdfButtonComponent,
    MatProgressSpinner,
    MatIconButton,
    MatMenuTrigger,
    MatIcon,
    MatDivider,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    BrowserAutoFillDisabledDirective,
    MatError,
    InfosCardComponent,
    NgTemplateOutlet,
    MatButton,
    MatCheckbox,
    InfosGuidanceComponent,
    MatTabGroup,
    MatTab,
    MatTabLabel,
    MatCard,
    NgOptimizedImage,
    EstimagePayGuidanceComponent,
    FactoringCompanyGuidanceComponent,
    NgClass,
    MatSuffix,
    CdkTextareaAutosize,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatFooterCellDef,
    MatFooterCell,
    MatPrefix,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatFooterRowDef,
    MatFooterRow,
    MatMenu,
    MatMenuContent,
    MatMenuItem,
    InfosContentComponent,
    DecimalPipe,
    DatePipe,
    TimePipe,
    GetControlPipe
  ]
})
export class InvoiceCurtainComponent implements OnInit, OnChanges {
  @Input() document: DocumentInvoiceItem;
  @Input() isSentToInsurerTray = false;
  @Input() shouldCollapseCurtain: boolean;
  @Input() lastExcessChangeTimestamp: number;
  @Input() repairerSiteId: number;
  @Output() refreshInvoice: EventEmitter<any> = new EventEmitter();
  @Output() validateField: EventEmitter<any> = new EventEmitter();
  @Output() disableSendButton: EventEmitter<boolean> = new EventEmitter();
  @Output() synchronizeOwnerContribution: EventEmitter<Job> = new EventEmitter();
  @ViewChild('panel', { static: false }) panel: MatExpansionPanel;

  Math = Math;
  invoice: Invoice;
  invoiceForm: UntypedFormGroup;
  labourColumnHeaders = ['description', 'assessedAmount', 'invoicedAmount', 'approvedAmount'];
  labourDataSource = new MatTableDataSource<any | GroupBy>([]);
  partColumnHeaders = ['partDescriptionNumber', 'partSource', 'assessorNote', 'assessedAmount', 'invoicedAmount'];
  partDataSource = new MatTableDataSource<any | GroupBy>([]);
  subletsColumnHeaders = ['description', 'note', 'assessedAmount', 'invoicedAmount', 'approvedAmount'];
  subletsDataSource = new MatTableDataSource<any | GroupBy>([]);

  totalPayable: number;
  paymentByCheque: boolean;
  usingFactoringCompany: WritableSignal<boolean> = signal(false);
  selectedFactoringCompany: WritableSignal<FactoringCompany> = signal(null);
  isLargeScreen = false;
  currentUser: User;
  timezone: string;
  loadingPDF = false;
  atLeastOneComment = false;
  bankingDetailsBanner: WritableSignal<BankingDetailsBanner> = signal(null);

  #dynamicDialogService = inject(DynamicDialogService);
  #injector = inject(Injector);
  #invoiceStore = inject(InvoiceStore);
  #breakpointObserver = inject(BreakpointObserverHelper);
  #fb = inject(UntypedFormBuilder);
  #decPipe = inject(DecimalPipe);
  #userService = inject(UserService);
  #dialog = inject(MatDialog);
  #message = inject(MessagingService);
  toolbarService = inject(ToolbarService);
  dateInputHelper = inject(DateInputHelper);
  #currentUserService = inject(CurrentUserService);
  #modalConfigsHelper = inject(ModalConfigsHelper);
  sysAdminService = inject(SysAdminService);
  userService = inject(UserService);
  jobViewerStore = inject(JobViewerStore);
  #destroyRef = inject(DestroyRef);

  constructor() {
    effect(() => {
      if (this.#invoiceStore.refreshInvoice()) {
        this.validateField.emit();
        this.refreshInvoice.emit();
        this.#invoiceStore.refreshInvoice.set(false);
      }
    });
  }

  get invoiceNumber() {
    return this.invoiceForm.get('invoiceNumber');
  }

  get preparedBy() {
    return this.invoiceForm.get('preparedBy');
  }

  get ownerContribution() {
    return this.invoiceForm.get('ownerContribution');
  }

  get labourItems() {
    return this.invoiceForm.get('labourItems') as UntypedFormArray;
  }

  get partItems() {
    return this.invoiceForm.get('partItems') as UntypedFormArray;
  }

  get subletItems() {
    return this.invoiceForm.get('subletItems') as UntypedFormArray;
  }

  get factoringCompany() {
    return this.invoiceForm.get('factoringCompany');
  }

  get repairerComment() {
    return this.invoiceForm.get('repairerComment');
  }

  ngOnInit(): void {
    this.currentUser = this.#currentUserService.currentUserValue;
    this.timezone = this.#currentUserService?.timeZone;
    this.invoice = this.document.document;

    this.#breakpointObserver
      .observeMinWidth()
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: (result) => {
          this.isLargeScreen = result;
          this.partColumnHeaders = this.isLargeScreen
            ? invoiceCurtainPartColumnHeadersLargeScreen
            : invoiceCurtainPartColumnHeadersSmallScreen;
        }
      });
    this.updateBankingDetailsBanner(this.invoice);
    this.groupDataSource(this.invoice.labour, this.labourDataSource);
    this.groupDataSource(this.invoice.parts, this.partDataSource);
    this.groupDataSource(this.invoice.sublets, this.subletsDataSource);

    this.atLeastOneComment = !![...this.invoice.labour, ...this.invoice.parts, ...this.invoice.sublets]
      .map(({ costControlComment }) => costControlComment)
      .filter(Boolean).length;

    this.invoiceForm = this.#fb.group({
      ownerContribution: [
        this.invoice.ownerContribution,
        Validators.pattern('\\-?(\\d+|\\d{1,3}(,\\d{3})*)(\\.\\d{1,2})?')
      ],
      invoiceNumber: [this.invoice.invoiceNumber],
      preparedBy: [this.invoice.preparedBy],
      labourItems: new UntypedFormArray([]),
      partItems: new UntypedFormArray([]),
      subletItems: new UntypedFormArray([]),
      factoringCompany: [null],
      repairerComment: this.invoice.repairerComment ?? null
    });

    if (this.document.submitted) {
      this.invoiceNumber.disable();
      this.preparedBy.disable();
      this.ownerContribution.disable();
      this.factoringCompany.disable();
      this.repairerComment.disable();
    }

    this.paymentByCheque = this.invoice.paymentByCheque;

    this.#initializeFactoringCompany();

    this.populateLabourItems();
    this.populatePartItems();
    this.populateSubletItems();
    this.setTotalPayable();
    this.formatOwnerContribution();

    if (this.document.submitted) {
      this.labourItems.controls.forEach((item) => item.disable());
      this.partItems.controls.forEach((item) => item.disable());
      this.subletItems.controls.forEach((item) => item.disable());
    }

    this.ownerContribution.valueChanges.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => {
      if (this.ownerContribution.invalid) {
        this.ownerContribution.markAsTouched();
        this.disableSendButton.emit(true);
      }
    });

    this.ownerContribution.valueChanges
      .pipe(debounceTime(250), distinctUntilChanged(), takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        if (this.ownerContribution.dirty && this.ownerContribution.valid) {
          this.disableSendButton.emit(false);
          this.update('ownerContribution');
        }
      });

    this.repairerComment.valueChanges
      .pipe(debounceTime(750), distinctUntilChanged(), takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        this.update('repairerComment');
      });

    this.invoiceNumber.valueChanges
      .pipe(debounceTime(750), distinctUntilChanged(), takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        this.update('invoiceNumber');
        setTimeout(() => this.validateField.emit(this.invoiceNumber.invalid ? this.invoiceNumber : null), 1000);
      });

    this.preparedBy.valueChanges
      .pipe(debounceTime(750), distinctUntilChanged(), takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        if (this.preparedBy.valid) {
          this.update('preparedBy');
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.document && !changes.document.firstChange) {
      this.invoice = this.document.document;
      this.formatOwnerContribution();
      this.populateLabourItems();
      this.populatePartItems();
      this.populateSubletItems();
      this.setTotalPayable();
      this.updateBankingDetailsBanner(this.invoice);
    }
    if (changes.shouldCollapseCurtain && !changes.shouldCollapseCurtain.firstChange) {
      if (changes.shouldCollapseCurtain.currentValue) {
        this.panel.close();
      }
    }
  }

  #initializeFactoringCompany(): void {
    // ToDo move this call to the constructor of the invoiceStore when we remove providerIn: root
    this.#invoiceStore.getFactoringCompanies();
    this.usingFactoringCompany.set(this.invoice.factoryCompanyEnable);

    if (this.usingFactoringCompany() && this.#invoiceStore.factoringCompanies().length) {
      this.selectedFactoringCompany.set(
        this.#invoiceStore.factoringCompanies().find((company) => company.companyName === this.invoice.paymentTo)
      );
    }

    effect(
      () => {
        // If the list was empty at initialization
        if (this.#invoiceStore.factoringCompanies().length) {
          untracked(
            () =>
              this.usingFactoringCompany() &&
              this.selectedFactoringCompany.set(
                this.#invoiceStore
                  .factoringCompanies()
                  .find((company) => company.companyName === this.invoice.paymentTo)
              )
          );
        }
      },
      { injector: this.#injector }
    );
  }

  updateBankingDetailsBanner(details: Invoice | BankingDetailsBanner): void {
    const { bsb, accountName, accountNumber, repairerName, repairerAbn } = details;

    this.bankingDetailsBanner.set({
      bsb,
      accountName,
      accountNumber,
      repairerName,
      repairerAbn
    });
  }

  openInvoiceReport(): void {
    this.loadingPDF = true;
    this.#userService.getJobReport(`/jobs/${this.invoice.jobId}/reports/invoice/${this.invoice.id}`).subscribe({
      error: () => {
        this.#message.error('There was an error loading the invoice PDF.');
        this.loadingPDF = false;
      },
      complete: () => (this.loadingPDF = false)
    });
  }

  addBankingDetails(): void {
    const dialogConfig: MatDialogConfig = wideModalConfig;
    dialogConfig.data = { username: this.currentUser.username };
    this.#dialog
      .open(EditBankingDetailsComponent, dialogConfig)
      .afterClosed()
      .subscribe((result) => {
        if (result === 'Success') {
          this.#message.confirm('Banking Details Edited');
          this.validateField.emit();
          this.refreshInvoice.emit();
        }
      });
  }

  groupDataSource(
    arrayToGroup: InvoiceLabour[] | InvoicePart[] | InvoiceSublet[],
    dataSource: MatTableDataSource<any | GroupBy>
  ): void {
    this.getGroups(arrayToGroup).forEach((group) => {
      const header = {} as GroupBy;
      header.quoteNumber = group.key;
      header.isGroupBy = true;
      header.quoteVersion = group.list[0].originalQuote ? 'Quote' : 'Supplement';
      dataSource.data.push(header);
      group.list.forEach((item) => dataSource.data.push(item));
    });
  }

  formatOwnerContribution(): void {
    this.ownerContribution.setValue(
      this.invoice.ownerContribution ? this.#decPipe.transform(this.invoice.ownerContribution, '1.2-2') : '',
      { emitEvent: false }
    );
    this.ownerContribution.setErrors(null);
  }

  populateLabourItems(): void {
    this.invoice.labour.forEach((labour) => {
      const labourControl = this.#fb.group({
        id: labour.id,
        invoicedAmount: [this.#decPipe.transform(labour.invoicedAmount, '1.2-2')]
      });
      this.labourItems.push(labourControl);
    });
  }

  populatePartItems(): void {
    this.invoice.parts.forEach((part) => {
      const partControl = this.#fb.group({
        id: part.id,
        invoicedAmount: [this.#decPipe.transform(part.invoicedAmount, '1.2-2')]
      });
      this.partItems.push(partControl);
    });
  }

  populateSubletItems(): void {
    this.invoice.sublets.forEach((sublet) => {
      const subletControl = this.#fb.group({
        id: sublet.id,
        invoicedAmount: [this.#decPipe.transform(sublet.invoicedAmount, '1.2-2')]
      });
      this.subletItems.push(subletControl);
    });
  }

  setTotalPayable(): void {
    if (['CREATED', 'PENDING_FOR_REVIEW', 'REJECTED'].includes(this.invoice.status)) {
      this.totalPayable = this.invoice.amountTotalPayableInvoiced;

      if (!this.document.submitted) {
        this.jobViewerStore.setUnsubmittedInvoiceTotalPayable(this.totalPayable);
      }
    } else if (['APPROVED', 'ADJUSTED'].includes(this.invoice.status)) {
      this.totalPayable = this.invoice.amountTotalPayableAccepted;

      if (!this.document.submitted) {
        this.jobViewerStore.setUnsubmittedInvoiceTotalPayable(this.totalPayable);
      }
    }
  }

  setPaymentByCheque(checked: boolean): void {
    this.paymentByCheque = checked;
    this.update('paymentByCheque');
  }

  update(fieldName: string): void {
    let fieldValue;

    if (fieldName === 'factoringCompany') {
      fieldValue = this.selectedFactoringCompany()?.id ?? '';
    } else if (fieldName === 'paymentByCheque') {
      fieldValue = this.paymentByCheque;
    } else if (fieldName === 'ownerContribution') {
      fieldValue = this.ownerContribution.value
        ? this.ownerContribution.value.toString().replace(',', '')
        : this.ownerContribution.value;
    } else {
      fieldValue = this.invoiceForm.controls[fieldName].value;
    }

    if (fieldName === 'ownerContribution' && this.invoiceForm.controls[fieldName].valid) {
      this.invoice.ownerContribution = +fieldValue;
      this.setTotalPayable();
      this.toolbarService.setCloudState('SAVING');
      this.#userService.updateJob(fieldValue, this.invoice.jobId, fieldName).subscribe({
        next: (job: Job) => {
          this.toolbarService.setCloudState('RESTING');
          this.synchronizeOwnerContribution.emit(job);
          this.refreshInvoice.emit();
        },
        error: () => {
          this.#message.error('Could not update field.');
          this.toolbarService.setCloudState('RESTING');
        }
      });
    } else {
      this.toolbarService.setCloudState('SAVING');
      this.#userService.updateInvoice(fieldValue, this.invoice.id, fieldName).subscribe({
        next: () => this.toolbarService.setCloudState('RESTING'),
        error: (error) => {
          if (error.error === 'DUPLICATE_INVOICE_NUMBER') {
            this.invoiceNumber.setErrors({ duplicateInvoice: true });
          } else {
            this.#message.error('Could not add invoice.');
          }
          this.toolbarService.setCloudState('RESTING');
          if (fieldName === 'factoringCompany') {
            this.selectedFactoringCompany.set(null);
          }
        },
        complete: () => this.refreshInvoice.emit()
      });
    }
  }

  updateJob(fieldName: string): void {
    const fieldValue = this.invoiceForm.controls[fieldName].value;

    if (this.invoiceForm.controls[fieldName].valid) {
      this.toolbarService.setCloudState('SAVING');
      this.#userService.updateJob(fieldValue, this.invoice.jobId, fieldName).subscribe({
        next: () => this.toolbarService.setCloudState('RESTING'),
        error: () => {
          this.#message.error('Could not update field.');
          this.toolbarService.setCloudState('RESTING');
          this.invoiceForm.controls[fieldName].setValue(this.invoice[fieldName]);
        },
        complete: () => {
          this.refreshInvoice.emit();
          this.invoiceForm.controls[fieldName].markAsPristine();
        }
      });
    }
  }

  updateParts(partId: number): void {
    const partCtrl = this.partItems.controls.find((item) => item.value.id === partId);
    const partValue = partCtrl.get('invoicedAmount');
    partValue.setValue(partCtrl.get('invoicedAmount').value.toString().replace(/,/g, ''));
    partValue.setValidators(Validators.pattern('\\-?\\d*\\.?\\d{1,2}'));
    partValue.updateValueAndValidity();
    if (partValue) {
      if (partValue.value === '') {
        partValue.setValue('0.00');
      } else {
        const amount = partValue.value.toString().replace(',', '');
        partValue.setValue(amount);
      }
      if (partValue && partValue.valid) {
        this.disableSendButton.emit(false);
        this.toolbarService.setCloudState('SAVING');
        this.#userService.updateInvoicePart(partValue.value, partId).subscribe({
          next: (invoice: Invoice) => {
            this.invoice = invoice;
            this.toolbarService.setCloudState('RESTING');
          },
          error: () => {
            this.#message.error('Could not update field.');
            this.toolbarService.setCloudState('RESTING');
          },
          complete: () => {
            this.setTotalPayable();
            partValue.setValue(this.#decPipe.transform(partValue.value, '1.2-2'));
            partValue.setErrors(null);
          }
        });
      }
    } else {
      this.disableSendButton.emit(true);
    }
  }

  updateSublet(subletId: number): void {
    const subletCtrl = this.subletItems.controls.find((item) => item.value.id === subletId);
    const subletValue = subletCtrl.get('invoicedAmount');
    subletValue.setValue(subletCtrl.get('invoicedAmount').value.toString().replace(/,/g, ''));
    subletValue.setValidators(Validators.pattern('\\-?\\d*\\.?\\d{1,2}'));
    subletValue.updateValueAndValidity();
    if (subletValue) {
      if (subletValue.value === '') {
        subletValue.setValue('0.00');
      } else {
        const amount = subletValue.value.toString().replace(',', '');
        subletValue.setValue(amount);
      }
    }
    if (subletValue && subletValue.valid) {
      this.disableSendButton.emit(false);
      this.toolbarService.setCloudState('SAVING');
      this.#userService.updateInvoiceSublet(subletValue.value, subletId).subscribe({
        next: (invoice: Invoice) => {
          this.invoice = invoice;
          this.toolbarService.setCloudState('RESTING');
        },
        error: () => {
          this.#message.error('Could not update field.');
          this.toolbarService.setCloudState('RESTING');
        },
        complete: () => {
          this.setTotalPayable();
          subletValue.setValue(this.#decPipe.transform(subletValue.value, '1.2-2'));
          subletValue.setErrors(null);
        }
      });
    } else {
      this.disableSendButton.emit(true);
    }
  }

  updateLabour(labourId: number): void {
    const labourCtrl = this.labourItems.controls.find((item) => item.value.id === labourId);
    const labourValue = labourCtrl.get('invoicedAmount');
    labourValue.setValue(labourCtrl.get('invoicedAmount').value.toString().replace(/,/g, ''));
    labourValue.setValidators(Validators.pattern('\\-?\\d*\\.?\\d{1,2}'));
    labourValue.updateValueAndValidity();
    if (labourValue) {
      if (labourValue.value === '') {
        labourValue.setValue('0.00');
      }
    } else {
      const amount = labourValue.value.toString().replace(',', '');
      labourValue.setValue(amount);
    }
    if (labourValue && labourValue.valid) {
      this.disableSendButton.emit(false);
      this.toolbarService.setCloudState('SAVING');
      this.#userService.updateInvoiceLabour(labourValue.value, labourId).subscribe({
        next: (invoice: Invoice) => {
          this.invoice = invoice;
          this.toolbarService.setCloudState('RESTING');
        },
        error: () => {
          this.#message.error('Could not update field.');
          this.toolbarService.setCloudState('RESTING');
        },
        complete: () => {
          this.setTotalPayable();
          labourValue.setValue(this.#decPipe.transform(labourValue.value, '1.2-2'));
          labourValue.setErrors(null);
        }
      });
    } else {
      this.disableSendButton.emit(true);
    }
  }

  getGroups(arrayToGroup: InvoiceLabour[] | InvoicePart[] | InvoiceSublet[]) {
    const group = this.groupBy(arrayToGroup, 'quoteNumber');
    return Object.keys(group).map((key) => ({ key, list: group[key] }));
  }

  groupBy(data, property) {
    return data.reduce((acc, obj) => {
      const key = obj[property];
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(obj);
      return acc;
    }, {});
  }

  isGroup(index: number, item: any): boolean {
    return item.isGroupBy;
  }

  useFactoringCompany(): void {
    const dialogConfig: MatDialogConfig = {
      ...wideModalConfig,
      data: {
        title: 'Select Factoring Company',
        action: 'SELECT',
        displayClose: true,
        component: UseFactoringCompanyModalComponent,
        selectFactoringCompanyId: this.selectedFactoringCompany()?.id || null
      }
    };
    this.#dynamicDialogService
      .openDialog(dialogConfig)
      .afterClosed()
      .subscribe(({ selectedFactoringCompany }) => {
        if (selectedFactoringCompany !== undefined) {
          this.usingFactoringCompany.set(selectedFactoringCompany !== null);
          this.selectedFactoringCompany.set(selectedFactoringCompany);
          this.validateField.emit();
          this.update('factoringCompany');
        }
      });
  }

  useEstimagePay(): void {
    const accountSelected: Omit<DisbursementAccount, 'accountId'> = this.invoice.estimagePayDisbursementAccountName
      ? {
          accountName: this.invoice.estimagePayDisbursementAccountName,
          accountNumber: this.invoice.estimagePayDisbursementAccountNumber,
          bsb: this.invoice.estimagePayDisbursementBsb
        }
      : null;
    const estImagePayRequest: Omit<EstImagePayRequest, 'disbursementAccounts'> = {
      invoiceNumber: this.invoice.invoiceNumber,
      invoiceId: this.invoice.id,
      amountRequested: this.totalPayable,
      accountSelected
    };

    const dialogConfig = {
      ...wideModalConfig,
      data: {
        title: 'Request Payment through EstImage Pay',
        action: 'REQUEST PAYMENT',
        displayClose: true,
        component: UseEstimagePayModalComponent,
        estImagePayRequest
      }
    };

    this.#invoiceStore.getDisbursementAccounts(dialogConfig);
  }

  removeFactoringCompany(): void {
    this.usingFactoringCompany.set(false);
    this.selectedFactoringCompany.set(null);
    this.validateField.emit();
    this.update('factoringCompany');
  }

  deleteInvoice(id: number, jobId: number): void {
    this.toolbarService.setCloudState('SAVING');
    this.#userService.deleteUnsubmittedInvoice(id, jobId).subscribe({
      next: () => this.toolbarService.setCloudState('RESTING'),
      error: () => {
        this.#message.error('Could not delete invoice.');
        this.toolbarService.setCloudState('RESTING');
      },
      complete: () => {
        this.refreshInvoice.emit(true);
        this.#message.confirm('Invoice deleted.');
      }
    });
  }

  confirmDeleteInvoice(id: number, jobId: number): void {
    const data = { title: 'Delete Invoice', alertMessage: 'Invoice will be deleted.', confirmButton: 'DELETE' };
    this.#dialog
      .open(ConfirmBoxComponent, this.#modalConfigsHelper.buildMediumModalConfig(data, smallModalConfig))
      .afterClosed()
      .subscribe((result) => {
        if (result === 'DELETE') {
          this.deleteInvoice(id, jobId);
        }
      });
  }

  cancelInvoice(invoiceId: number): void {
    const data = {
      title: 'Cancel Invoice',
      alertMessage: 'Are you sure you wish to cancel your invoice?',
      confirmButton: 'CONFIRM',
      hideCannotBeUndone: true,
      biggerButtons: true
    };
    this.#dialog
      .open(ConfirmBoxComponent, this.#modalConfigsHelper.buildMediumModalConfig(data, mediumModalConfig))
      .afterClosed()
      .subscribe({
        next: (result) => {
          if (result === 'CONFIRM') {
            this.#userService.cancelInvoice(invoiceId).subscribe({
              next: () => {
                this.#message.confirm('Invoice cancelled');
                this.refreshInvoice.emit();
              },
              error: () => this.#message.error('Could not cancel invoice')
            });
          }
        }
      });
  }
}

export interface GroupBy {
  quoteNumber: string;
  quoteVersion: string;
  isGroupBy: boolean;
}
